import { WarrantProgramDocument } from '../../../WarrantProgram.graphql';
import { GqlWarrantProgram } from '../../../../../graphql/base-types.graphql';
import { Alert } from '@startuptools/ui/Alert';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans, useLingui } from '@lingui/react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { BigNumber } from 'bignumber.js';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { useWpSetStrikePriceMutation } from '../../../../../graphql/react-operations';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../../components/steppers/common/StepperStepContentContext';
import { ErrorDisplay } from '../../../../../components/error-display/ErrorDisplay';
import { Link } from '@startuptools/ui/Link';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { GqlArticlesOfAssoc } from '../../../../../graphql/base-types.graphql';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { useEffect } from 'react';
import { isNil } from 'lodash-es';
import { InfoTooltip } from '@startuptools/ui/InfoTooltip';
import { Card } from '@startuptools/ui/Card';
import { useFieldSync } from '@startuptools/common/react-hooks';

type FormValues = {
  strikePrice: NonNullable<GqlWarrantProgram['strikePrice']>;
  useNetStrike: NonNullable<GqlWarrantProgram['useNetStrike']>;
  percent: string;
};

export const WpStepStrikePrice = ({
  warrantProgram,
}: {
  warrantProgram: Pick<
    GqlWarrantProgram,
    | 'id'
    | 'companyId'
    | 'strikePrice'
    | 'useNetStrike'
    | 'valuationNumberOfShares'
    | 'valuationCompanyValue'
    | 'warrantsForQeso'
  > & {
    articlesOfAssoc?: Pick<
      GqlArticlesOfAssoc,
      'id' | 'changeNumberOfShares' | 'numberOfSharesBefore' | 'numberOfSharesAfter'
    > | null;
  };
}) => {
  const { i18n } = useLingui();
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();
  const [doSubmit, { loading }] = useWpSetStrikePriceMutation({
    refetchQueries: [WarrantProgramDocument],
    awaitRefetchQueries: true,
  });

  const form = useForm<FormValues>({
    defaultValues: {
      strikePrice: warrantProgram.strikePrice ?? '',
      useNetStrike: warrantProgram.useNetStrike,
    },
  });

  const strikePrice = form.watch('strikePrice');

  const onSubmit: SubmitHandler<FormValues> = async values => {
    await doSubmit({
      variables: {
        companyId: warrantProgram.companyId,
        input: { id: warrantProgram.id, strikePrice: values.strikePrice, useNetStrike: values.useNetStrike },
      },
    });
    return stepNext();
  };

  useEffect(() => {
    if (warrantProgram.strikePrice) {
      form.setValue('strikePrice', warrantProgram.strikePrice);
    }
  }, [warrantProgram.strikePrice, form]);

  if (isNil(warrantProgram.strikePrice) && !warrantProgram.warrantsForQeso) {
    return (
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="What should the price per share be?" />
        </Typography>
        <Alert>
          <Typography component="span" level="body-sm">
            <Trans id="Fill out the step about company valuation first" />
          </Typography>
        </Alert>
      </Stack>
    );
  }

  const currentShareValue = new BigNumber(warrantProgram.valuationCompanyValue ?? '1').div(
    warrantProgram.valuationNumberOfShares ?? '1',
  );
  const aoa = warrantProgram.articlesOfAssoc;
  const aoaShareValue = aoa?.changeNumberOfShares
    ? new BigNumber(aoa.numberOfSharesBefore).div(aoa.numberOfSharesAfter!).times(currentShareValue)
    : null;

  const shareValue = aoaShareValue ? aoaShareValue : currentShareValue;

  const newCompanyValue = !strikePrice
    ? '0'
    : aoa?.changeNumberOfShares
      ? new BigNumber(strikePrice).times(aoa.numberOfSharesAfter ?? '0')
      : new BigNumber(strikePrice).times(warrantProgram.valuationNumberOfShares ?? '0');

  useFieldSync({
    form,
    fields: {
      strikePrice: value => {
        return {
          percent: new BigNumber(value).minus(shareValue).div(shareValue).times(100).toString(),
        };
      },
      percent: value => {
        return {
          strikePrice: new BigNumber(value).div(100).times(shareValue).plus(shareValue).toString(),
        };
      },
    },
  });

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="What should the price per share be?" />
        </Typography>

        <Alert>
          <Typography component="span" level="body-sm">
            {warrantProgram.warrantsForQeso ? (
              <Trans id="This program backs a QESO program. The strike price should normally be the same as on the QESO program." />
            ) : (
              <Trans id='When the vesting period has passed, the warrant holder gets the option to buy one share for each warrant. The price per share is also called the "strike-price". The lower the strike-price is, the less money the warrant holder needs to pay then. But a lower strike-price also leads to a higher price for the warrant now. It is possible to change this later.' />
            )}
          </Typography>
        </Alert>

        {!warrantProgram.warrantsForQeso && (
          <Card sx={{ maxWidth: 300 }}>
            <Typography level="h4">
              <Trans id="Current Company Valuation" />
            </Typography>
            <Typography level="title-lg">
              <NumberFormat locale={i18n.locale} variant="currency" currency="SEK">
                {warrantProgram.valuationCompanyValue?.toString()}
              </NumberFormat>
            </Typography>
            <Typography level="body-sm">
              <Trans
                id="Share Price: <numberFormat>{shareValue}</numberFormat>"
                components={{ numberFormat: <NumberFormat locale={i18n.locale} variant="currency" currency="SEK" /> }}
                values={{ shareValue: currentShareValue.toString() }}
              />
            </Typography>
          </Card>
        )}

        {!warrantProgram.warrantsForQeso && (
          <Controller
            control={form.control}
            name="percent"
            render={({ field: { onChange, value, ...field } }) => (
              <FormControl sx={{ maxWidth: 220 }}>
                <FormLabel required>
                  <Trans id="Increase" />
                </FormLabel>
                <NumericFormatInput
                  {...field}
                  lang={i18n.locale}
                  value={value ?? '100'}
                  onChange={v => {
                    onChange(v.target.value);
                  }}
                  endDecorator="%"
                  required
                />
              </FormControl>
            )}
          />
        )}

        <Controller
          control={form.control}
          name="strikePrice"
          render={({ field: { onChange, value, ...field } }) => (
            <FormControl sx={{ maxWidth: 220 }}>
              <FormLabel required>
                <Trans id="Strike-Price" />
              </FormLabel>
              <NumericFormatInput
                {...field}
                lang={i18n.locale}
                value={value ?? '0'}
                onChange={v => {
                  onChange(v.target.value);
                }}
                endDecorator={<Trans id="SEK/share" />}
                required
              />
            </FormControl>
          )}
        />

        {!warrantProgram.warrantsForQeso && (
          <Stack direction="row" gap={1} alignItems="center">
            <Controller
              control={form.control}
              name="useNetStrike"
              render={({ field: { onChange, value, ...field } }) => (
                <Checkbox
                  {...field}
                  checked={value}
                  onChange={v => onChange(v.target.checked)}
                  label={<Trans id="Use Net-strike" />}
                />
              )}
            />
            <InfoTooltip>
              <Typography component="span" level="body-sm">
                <Trans
                  id="Read more about net-strike <link>here</link>"
                  components={{
                    link: (
                      <Link href="https://startuptools.org/se/nettostrike-for-teckningsoptioner/" target="_blank" />
                    ),
                  }}
                />
              </Typography>
            </InfoTooltip>
          </Stack>
        )}

        {!warrantProgram.warrantsForQeso && (
          <Alert variant="outlined">
            <Typography component="span" level="body-sm">
              <Trans
                id='The subscription price means that the company must be worth at least <bold><numberFormat>{newCompanyValue}</numberFormat></bold> (subscription price times the number of shares) in order for the warrants to be worth being used to subscribe for shares (i.e. that the warrants are "in the money").'
                components={{
                  numberFormat: <NumberFormat locale={i18n.locale} variant="currency" currency="SEK" />,
                  bold: <b />,
                }}
                values={{ newCompanyValue: newCompanyValue.toString() }}
              />
            </Typography>
          </Alert>
        )}

        {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

        <Box>
          <Button type="submit" loading={loading}>
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
